import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { User, Mail, Phone, Calendar, Heart, MapPin, ExternalLink, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";

interface Visitor {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  created_at: string;
  user_id: string | null;
}

interface FavoriteEvent {
  id: string;
  title: string;
  title_ar: string | null;
  category: string | null;
  date: string | null;
  location: string | null;
  location_ar: string | null;
  image_url: string | null;
  external_link: string | null;
}

interface UserDetailsDialogProps {
  user: Visitor | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const UserDetailsDialog = ({ user, open, onOpenChange }: UserDetailsDialogProps) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [favoriteEvents, setFavoriteEvents] = useState<FavoriteEvent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.user_id && open) {
      fetchFavoriteEvents(user.user_id);
    } else {
      setFavoriteEvents([]);
    }
  }, [user, open]);

  const fetchFavoriteEvents = async (userId: string) => {
    setLoading(true);
    try {
      // First get favorite event IDs
      const { data: favorites, error: favError } = await supabase
        .from("favorite_events")
        .select("event_id")
        .eq("user_id", userId);

      if (favError) {
        console.error("Error fetching favorites:", favError);
        setFavoriteEvents([]);
        return;
      }

      if (!favorites || favorites.length === 0) {
        setFavoriteEvents([]);
        return;
      }

      // Then get the event details
      const eventIds = favorites.map(f => f.event_id);
      const { data: events, error: eventsError } = await supabase
        .from("scraped_events")
        .select("id, title, title_ar, category, date, location, location_ar, image_url, external_link")
        .in("id", eventIds);

      if (eventsError) {
        console.error("Error fetching events:", eventsError);
        setFavoriteEvents([]);
        return;
      }

      setFavoriteEvents(events || []);
    } catch (error) {
      console.error("Error:", error);
      setFavoriteEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMM yyyy, HH:mm", {
      locale: isRTL ? ar : enUS,
    });
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-2xl max-h-[85vh] overflow-y-auto ${isRTL ? "rtl" : "ltr"}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <span>{isRTL ? "تفاصيل المستخدم" : "User Details"}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* User Info */}
          <Card className="border shadow-sm">
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">{isRTL ? "الاسم" : "Name"}</p>
                  <p className="font-medium">{user.full_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">{isRTL ? "البريد الإلكتروني" : "Email"}</p>
                  <p className="font-medium">{user.email}</p>
                </div>
              </div>

              {user.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">{isRTL ? "رقم الهاتف" : "Phone"}</p>
                    <p className="font-medium">{user.phone}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">{isRTL ? "تاريخ التسجيل" : "Registration Date"}</p>
                  <p className="font-medium">{formatDate(user.created_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Favorite Events */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              {isRTL ? "الفعاليات المفضلة" : "Favorite Events"}
              <Badge variant="secondary" className="ml-2">
                {favoriteEvents.length}
              </Badge>
            </h3>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : favoriteEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                <Heart className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>{isRTL ? "لا توجد فعاليات مفضلة" : "No favorite events"}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {favoriteEvents.map((event) => (
                  <Card key={event.id} className="border shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-3">
                      <div className="flex gap-3">
                        {event.image_url && (
                          <img
                            src={event.image_url}
                            alt={isRTL ? event.title_ar || event.title : event.title}
                            className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate">
                            {isRTL ? event.title_ar || event.title : event.title}
                          </h4>
                          {event.category && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {event.category}
                            </Badge>
                          )}
                          <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                            {event.date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {event.date}
                              </span>
                            )}
                            {(event.location || event.location_ar) && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {isRTL ? event.location_ar || event.location : event.location}
                              </span>
                            )}
                          </div>
                        </div>
                        {event.external_link && (
                          <a
                            href={event.external_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailsDialog;
